// src/hooks/useGameClock.ts
import { useState, useEffect } from "react";
import { Piece } from "../types/Board";
import { analyzeOpponentMaterial } from "../utils/gameHelpers.ts";

interface UseGameClockProps {
  clockEnabled: boolean;
  currentTurn: "white" | "black";
  isReviewingHistory: boolean;
  pieces: Map<string, Piece>;
  boardRows: number;
  boardCols: number;
  onTimeout: (
    winner: string,
    loser: string,
    result: "win" | "draw",
    opponentMaterial: string
  ) => void;
}

export const useGameClock = ({
  clockEnabled,
  currentTurn,
  isReviewingHistory,
  pieces,
  boardRows,
  boardCols,
  onTimeout,
}: UseGameClockProps) => {
  const [clockDuration, setClockDuration] = useState<number>(30); // minutes
  const [whiteTimeLeft, setWhiteTimeLeft] = useState<number>(30 * 60); // seconds
  const [blackTimeLeft, setBlackTimeLeft] = useState<number>(30 * 60); // seconds
  const [clockRunning, setClockRunning] = useState<boolean>(false);
  const [clockStarted, setClockStarted] = useState<boolean>(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState<boolean>(false);
  const [timeoutResult, setTimeoutResult] = useState<{
    winner: string;
    loser: string;
    result: "win" | "draw";
    opponentMaterial: string;
  } | null>(null);
  const [showClockWarning, setShowClockWarning] = useState<boolean>(false);

  // Clock countdown effect
  useEffect(() => {
    if (!clockEnabled || !clockRunning || isReviewingHistory) return;

    const interval = setInterval(() => {
      if (currentTurn === "white") {
        setWhiteTimeLeft((prev) => {
          if (prev <= 0) {
            setClockRunning(false);
            handleTimeout("white");
            return 0;
          }
          return prev - 1;
        });
      } else {
        setBlackTimeLeft((prev) => {
          if (prev <= 0) {
            setClockRunning(false);
            handleTimeout("black");
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [clockEnabled, clockRunning, currentTurn, isReviewingHistory]);

  /**
   * Handle clock timeout for a player
   */
  const handleTimeout = (playerOutOfTime: "white" | "black") => {
    const opponent = playerOutOfTime === "white" ? "black" : "white";

    console.log(`⏰ ${playerOutOfTime} ran out of time!`);

    // For 8x8 and larger boards, check if opponent has mating material
    if (boardRows >= 8 && boardCols >= 8) {
      const opponentMaterial = analyzeOpponentMaterial(
        opponent,
        pieces,
        boardRows,
        boardCols
      );

      // Materials that cannot force checkmate
      const drawMaterials = [
        "K",
        "K+N",
        "K+B",
        "K+N+N",
        "K+B+B (opposite colors)",
      ];
      const isDraw = drawMaterials.includes(opponentMaterial);

      setTimeoutResult({
        winner: isDraw ? "Draw" : opponent,
        loser: playerOutOfTime,
        result: isDraw ? "draw" : "win",
        opponentMaterial: opponentMaterial,
      });

      if (isDraw) {
        console.log(
          `⚖️ Draw: ${opponent} has insufficient mating material (${opponentMaterial})`
        );
      } else {
        console.log(
          `✅ ${opponent} wins on time. Material: ${opponentMaterial}`
        );
      }

      // Call the onTimeout callback with result
      onTimeout(
        isDraw ? "Draw" : opponent,
        playerOutOfTime,
        isDraw ? "draw" : "win",
        opponentMaterial
      );
    } else {
      // For boards smaller than 8x8, always award win on timeout
      setTimeoutResult({
        winner: opponent,
        loser: playerOutOfTime,
        result: "win",
        opponentMaterial: "N/A (board smaller than 8x8)",
      });

      console.log(`✅ ${opponent} wins on time (small board)`);
      onTimeout(opponent, playerOutOfTime, "win", "N/A (board smaller than 8x8)");
    }

    setShowTimeoutModal(true);
  };

  /**
   * Start the clock (called on first move)
   */
  const startClock = () => {
    if (clockEnabled && !clockStarted) {
      setClockRunning(true);
      setClockStarted(true);
      console.log("⏰ Clock started");
    }
  };

  /**
   * Stop the clock
   */
  const stopClock = () => {
    setClockRunning(false);
    console.log("⏰ Clock stopped");
  };

  /**
   * Reset the clock to initial duration
   */
  const resetClock = () => {
    setClockRunning(false);
    setClockStarted(false);
    setWhiteTimeLeft(clockDuration * 60);
    setBlackTimeLeft(clockDuration * 60);
    console.log("⏰ Clock reset");
  };

  /**
   * Change clock duration (only works before clock starts)
   */
  const handleClockDurationChange = (minutes: number) => {
    setClockDuration(minutes);
    setWhiteTimeLeft(minutes * 60);
    setBlackTimeLeft(minutes * 60);
    setClockRunning(false);
    setClockStarted(false);
    console.log(`⏰ Clock duration set to ${minutes} minutes`);
  };

  /**
   * Toggle clock on/off
   */
  const toggleClock = (enabled: boolean) => {
    if (!enabled) {
      // Turning off clock - reset everything
      setClockRunning(false);
      setClockStarted(false);
      setWhiteTimeLeft(clockDuration * 60);
      setBlackTimeLeft(clockDuration * 60);
      console.log("⏰ Clock disabled");
    } else {
      console.log("⏰ Clock enabled");
    }
  };

  /**
   * Format time in MM:SS
   */
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    // State
    clockDuration,
    whiteTimeLeft,
    blackTimeLeft,
    clockRunning,
    clockStarted,
    showTimeoutModal,
    timeoutResult,
    showClockWarning,

    // Actions
    startClock,
    stopClock,
    resetClock,
    handleClockDurationChange,
    toggleClock,
    formatTime,

    // Setters
    setShowTimeoutModal,
    setShowClockWarning,
    setClockRunning,
    setClockStarted,
    setWhiteTimeLeft,
    setBlackTimeLeft,
  };
};
